import {
  OktonLoadingCard,
  OktonErrorCard,
  OktonSearchSelect,
  OktonPageHeader,
  OktonSearchFilter,
  OktonDataGrid,
  OktonEmptyState,
  OktonConfirmDialog,
  ColunaGrid,
  useGrid,
  TAMANHOS_PAGINA,
} from '@okton/platform-ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Coins, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import {
  Moeda,
  useDeleteMoeda,
  useMoedas,
} from '../../services/hooks/useCadastros';
import { useBancos } from '../../services/hooks/useContasBancarias';
import { colunasDataGrid } from '../../utils/colunasDataGrid';

// Sem coluna de Tipo de Título: a tela só lista Moeda do tipo Banco.
// Marcações não ordenam nem entram na busca: são dois selos, não um valor.
const COLUNAS: ColunaGrid<Moeda>[] = [
  {
    chave: 'id_moe',
    titulo: 'Código',
    valor: m => Number(m.id_moe),
    tipo: 'numero',
  },
  { chave: 'nome_moe', titulo: 'Nome', valor: m => m.nome_moe },
  { chave: 'nome_ban', titulo: 'Banco', valor: m => m.nome_ban },
  {
    chave: 'descricao_cts',
    titulo: 'Conta Bancária',
    valor: m => m.descricao_cts,
  },
  {
    chave: 'nomeplanodre_moe',
    titulo: 'Plano de Contas',
    valor: m => m.nomeplanodre_moe,
  },
  { chave: 'marcacoes', titulo: 'Marcações' },
];

const Moedas = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');

  const { data: moedas = [], isLoading, isError, refetch } = useMoedas();
  const deleteMutation = useDeleteMoeda();
  const toast = useToast();
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const [itemExcluir, setItemExcluir] = useState<string | null>(null);
  const [busca, setBusca] = useState('');

  // O Banco pode vir pronto na URL: é assim que a tela de Moedas da Conta
  // manda para cá já mostrando as Moedas do banco daquela Conta.
  const { search } = useLocation();
  const { data: bancos = [] } = useBancos();
  const [filtroBanco, setFiltroBanco] = useState(
    () => new URLSearchParams(search).get('banco') ?? '',
  );
  const daBanca = filtroBanco
    ? moedas.filter(m => m.banco_moe === filtroBanco)
    : moedas;
  const {
    ordenacao,
    alternarOrdenacao,
    itens: visiveis,
    filtrados,
    pagina,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
  } = useGrid(daBanca, COLUNAS, busca);

  const excluir = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir);
    try {
      await deleteMutation.mutateAsync(itemExcluir);
      setItemExcluir(null);
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'Erro ao excluir a Moeda.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Moedas"
        subtitle="Moedas do banco vinculadas às Contas Bancárias"
        actions={
          <>
            <Flex ml="auto" my="3" align="center" gridGap={2} flexWrap="wrap">
              <OktonSearchFilter
                value={busca}
                onChange={setBusca}
                placeholder="Buscar por nome, banco, conta..."
              />
              {/* Largura fixa: na natural (280px) os três controles não cabem
                  ao lado do título e descem, afastando o grid do cabeçalho. */}
              <Flex
                w="200px"
                borderWidth="1px"
                rounded="2xl"
                bg={bg}
                align="center"
              >
                <OktonSearchSelect
                  name="banco"
                  placeholder="Todos os bancos"
                  value={filtroBanco}
                  onChange={valor => setFiltroBanco(valor)}
                  options={[
                    ...bancos.map(b => ({
                      value: b.id_ban,
                      label: b.nome_ban,
                    })),
                  ]}
                />
              </Flex>
              <Flex
                borderWidth="1px"
                p="2"
                rounded="2xl"
                bg={bg}
                align="center"
                gridGap={2}
              >
                <Button
                  leftIcon={<Plus size={18} />}
                  colorScheme="blue"
                  onClick={() => history.push('/moedas/nova')}
                  minW="150px"
                >
                  Nova Moeda
                </Button>
              </Flex>
            </Flex>
          </>
        }
      />

      {isLoading ? (
        <Flex mt="1">
          <OktonLoadingCard h="200px" />
        </Flex>
      ) : isError ? (
        <Flex mt="1">
          <OktonErrorCard h="200px" onRetry={refetch} />
        </Flex>
      ) : (
        <Box bg={bg} mt="1" rounded="2xl">
          {filtrados.length === 0 ? (
            <OktonEmptyState
              icon={Coins}
              title={
                busca
                  ? 'Nada encontrado para a busca'
                  : 'Nenhuma moeda cadastrada'
              }
            />
          ) : (
            <OktonDataGrid
              rows={visiveis}
              columns={[
                ...colunasDataGrid(COLUNAS, ordenacao, alternarOrdenacao, {
                  nome_moe: m => <Box fontWeight="bold">{m.nome_moe}</Box>,
                  marcacoes: m => (
                    <Flex gridGap="1" flexWrap="wrap">
                      {m.principal_moe === '1' && (
                        <Badge colorScheme="purple" borderRadius="full" px="2">
                          Principal
                        </Badge>
                      )}
                      {m.visivel_moe === '1' && (
                        <Badge colorScheme="green" borderRadius="full" px="2">
                          Faturamento
                        </Badge>
                      )}
                    </Flex>
                  ),
                }),
                {
                  key: 'acoes',
                  header: null,
                  align: 'right',
                  render: m => (
                    <Flex justify="flex-end" gridGap="1">
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        leftIcon={<Pencil size={14} />}
                        onClick={() => history.push(`/moedas/${m.id_moe}`)}
                      >
                        Editar
                      </Button>
                      <IconButton
                        aria-label="Excluir"
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        icon={<Trash2 size={16} />}
                        isLoading={excluindo === m.id_moe}
                        onClick={() => setItemExcluir(m.id_moe)}
                      />
                    </Flex>
                  ),
                },
              ]}
              getRowId={m => m.id_moe}
              page={pagina + 1}
              pageSize={tamanhoPagina}
              total={filtrados.length}
              pageSizeOptions={TAMANHOS_PAGINA}
              onPageChange={nova => setPagina(nova - 1)}
              onPageSizeChange={setTamanhoPagina}
            />
          )}
        </Box>
      )}
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={excluir}
        title="Confirmar exclusão"
        description="Excluir esta Moeda do IFLEX?"
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
    </BankAppPage>
  );
};

export default Moedas;
